import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import AccordionGroup from "@/components/sections/AccordionGroup";
import heroImg from "@/assets/faq-hero.jpg";
import ctaBg from "@/assets/bg-exterior-2.jpg";

const buyingFaqs = [
  { title: "Do I need to get pre-approved before starting my home search?", content: "Yes. Pre-approval helps you understand your budget and shows sellers you're a serious buyer. We can connect you with trusted lenders to get started." },
  { title: "How long does the home buying process take?", content: "It varies, but most buyers can expect the process to take 30–60 days once an offer is accepted. The timeline depends on financing, inspections, and negotiations." },
  { title: "What should I look for when choosing a home?", content: "Beyond aesthetics, consider location, long-term value, property condition, and future resale potential. We help you evaluate all of these factors during your search." },
  { title: "Do you work with first-time buyers?", content: "Absolutely. We specialize in guiding first-time buyers through every step of the process—from understanding the market to closing on your first home." },
];

const sellingFaqs = [
  { title: "How do I know when it's the right time to sell?", content: "Timing depends on the market, your personal situation, and your financial goals. We provide honest guidance to help you make an informed decision." },
  { title: "What should I do to prepare my home for sale?", content: "We offer a full property preparation process—including cleaning, painting, and repairs—to ensure your home shows at its best. Preparation can significantly impact your final sale price." },
  { title: "How do you determine the right listing price?", content: "We use a combination of market analysis, comparable sales, and property condition to recommend a pricing strategy that positions your home competitively." },
  { title: "How long does it take to sell a home?", content: "It depends on the market and property, but most homes sell within 30–90 days when priced and prepared correctly." },
];

const investingFaqs = [
  { title: "Is real estate a good investment right now?", content: "Real estate remains one of the most reliable long-term investments. We help you evaluate opportunities based on your goals, timeline, and risk tolerance." },
  { title: "Do you help with investment properties?", content: "Yes. Whether you're looking for rental properties, multi-family units, or long-term investment opportunities, we provide strategic guidance to help you make informed decisions." },
];

const drFaqs = [
  { title: "Can I buy property in the Dominican Republic as a U.S. citizen?", content: "Yes. U.S. citizens can legally purchase property in the Dominican Republic. We guide you through the process and connect you with trusted local partners." },
  { title: "What types of properties are available?", content: "Options range from vacation homes and condos to land and investment properties. We help you identify the right fit based on your goals." },
  { title: "Do I need to travel to the Dominican Republic to buy property?", content: "While visiting is recommended, it's not always required. We can help facilitate remote transactions with trusted local partners and legal professionals." },
];

const prepFaqs = [
  { title: "What does property preparation include?", content: "Our property preparation services include professional cleaning, painting, and carpentry repairs—designed to get your home market-ready and maximize its value." },
  { title: "Do I need to prepare my home before listing it?", content: "We strongly recommend it. Homes that are professionally prepared tend to sell faster and for more. We handle the process so you don't have to." },
  { title: "How much does property preparation cost?", content: "Costs vary depending on the scope of work, but we provide transparent estimates and work within your budget to prioritize the improvements that will have the greatest impact." },
];

const consultationFaqs = [
  { title: "What happens during a consultation?", content: "We'll discuss your goals, answer your questions, and outline a clear plan of action—whether you're buying, selling, investing, or preparing a property." },
  { title: "Is there a fee for the initial consultation?", content: "No. Your initial consultation is complimentary. It's an opportunity for us to understand your needs and for you to learn how we can help." },
  { title: "How do I book a consultation?", content: "You can book a consultation directly through our website, or contact us by phone at (781) 632-3936 or email at Esther@hadassahhomesinc.com." },
];

const FAQ = () => {
  useEffect(() => {
    document.title = "FAQ | Frequently Asked Questions | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Find answers to common questions about buying, selling, investing, and property preparation with Hadassah Homes.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/faq";
  }, []);

  return (
    <SiteLayout>
      <PageHero eyebrow="Support" title="Frequently Asked Questions" subtitle="Answers to Common Questions About Buying, Selling, Investing, and More" backgroundImage={heroImg} />

      {/* SECTION 2 — INTRO */}
      <ContentSection>
        <div className="mx-auto max-w-prose space-y-6 text-lg md:text-xl leading-relaxed text-muted-foreground">
          <p>We know that navigating the real estate process comes with a lot of questions—and that's completely normal.</p>
          <p>Below, you'll find answers to some of the most common questions we receive from buyers, sellers, and investors. If you don't see what you're looking for, don't hesitate to reach out.</p>
          <p className="text-foreground font-medium">Have a specific question?</p>
          <Link to="/consultation" className="inline-block font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors">
            Book a Consultation →
          </Link>
        </div>
      </ContentSection>

      {/* SECTION 3 — BUYING A HOME */}
      <ContentSection variant="dark">
        <SectionHeader title="Buying a Home" />
        <div className="mx-auto max-w-3xl">
          <AccordionGroup items={buyingFaqs} />
        </div>
      </ContentSection>

      {/* SECTION 4 — SELLING YOUR HOME */}
      <ContentSection>
        <SectionHeader title="Selling Your Home" />
        <div className="mx-auto max-w-3xl">
          <AccordionGroup items={sellingFaqs} />
        </div>
      </ContentSection>

      {/* SECTION 5 — REAL ESTATE INVESTING */}
      <ContentSection variant="dark">
        <SectionHeader title="Real Estate Investing" />
        <div className="mx-auto max-w-3xl">
          <AccordionGroup items={investingFaqs} />
        </div>
      </ContentSection>

      {/* SECTION 6 — DOMINICAN REPUBLIC INVESTING */}
      <ContentSection>
        <SectionHeader title="Dominican Republic Investing" />
        <div className="mx-auto max-w-3xl">
          <AccordionGroup items={drFaqs} />
        </div>
      </ContentSection>

      {/* SECTION 7 — PROPERTY PREPARATION */}
      <ContentSection variant="dark">
        <SectionHeader title="Property Preparation" />
        <div className="mx-auto max-w-3xl">
          <AccordionGroup items={prepFaqs} />
        </div>
      </ContentSection>

      {/* SECTION 8 — CONSULTATIONS */}
      <ContentSection>
        <SectionHeader title="Consultations" />
        <div className="mx-auto max-w-3xl">
          <AccordionGroup items={consultationFaqs} />
        </div>
      </ContentSection>

      {/* SECTION 9 — FINAL CTA */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <img src={ctaBg} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative z-10 mx-auto max-w-2xl px-6 text-center space-y-6">
          <h2 className="font-headline text-4xl md:text-5xl text-white leading-tight">Still Have Questions?</h2>
          <p className="font-body text-white/80 text-base md:text-lg leading-relaxed">
            We're here to help. Whether you're buying, selling, investing, or preparing a property—reach out and let's talk.
          </p>
          <div className="pt-2">
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 bg-accent px-8 py-4 font-body text-sm uppercase tracking-[0.12em] text-accent-foreground transition-colors hover:bg-accent/90"
            >
              Book a Consultation →
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default FAQ;
